import {chain, findLast, get, orderBy} from "lodash";
import {makeAutoObservable, observable} from "mobx";
import {inject, injectable} from "inversify";
import {ViewController} from "data/types/structure";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IDivision, IDivisionsStore} from "data/stores/divisions/divisions.store";
import type {ISquad, ISquadsStore, ISquadWithResults} from "data/stores/squads/squads.store";
import type {ILightPredictionsStore} from "data/stores/light_predictions/light_predictions.store";
import type {IStandingsStore} from "data/stores/standings/standings.store";
import {GameSteps} from "data/enums";
import {ConnextraType, createConnextraScript} from "data/utils/createConnextraScript";
import type {IUserStore} from "data/stores/user/user.store";

interface IProps {
	step: GameSteps;
}

export interface ILPSliderCardController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;

	get divisionName(): string;

	get currentDivision(): IDivision | undefined;

	get squadsFromDivision(): ISquadWithResults[];

	get currentDivisionsStepValue(): number;

	get isCardDisabled(): boolean;

	onSelectSquad: (squadId: number) => void;
}

@injectable()
export class LPSliderCardController implements ILPSliderCardController {
	@observable private _step: GameSteps = GameSteps.NFC_EAST;

	get currentDivision() {
		return this._divisionStore.divisions.find(({abbreviation}) => abbreviation === this._step);
	}

	get divisionName() {
		return get(this.currentDivision, "name", "");
	}

	get squadsFromDivision() {
		const squadsArr = this._squadStore.list.filter(
			(squad) => squad.divisionId === this.currentDivision?.id
		);

		return this.getSquadsByDivisions(squadsArr);
	}

	get currentDivisionsStepValue() {
		const squad = this.squadsFromDivision.find(({squadId}) =>
			this._lpStore.lightPrediction[this.conferenceType]
				.slice(0, 4)
				.some((prediction) => prediction.squadId === squadId)
		);

		return squad?.squadId || 0;
	}

	get conferenceType() {
		return this._lpStore.getConferenceByType(this._step);
	}

	get isCardDisabled() {
		return this._lpStore.isCardDisabled;
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.DivisionsStore) private _divisionStore: IDivisionsStore,
		@inject(Bindings.SquadsStore) private _squadStore: ISquadsStore,
		@inject(Bindings.StandingsStore) private _standingsStore: IStandingsStore,
		@inject(Bindings.LightPredictionsStore) private _lpStore: ILightPredictionsStore
	) {
		makeAutoObservable(this);
	}

	init(param: IProps) {
		this._step = param.step;
	}

	private getSquadsByDivisions = (squads: ISquad[]) => {
		return chain(squads)
			.map((squad) => {
				const standing = findLast(
					orderBy(this._standingsStore.standings, "roundId"),
					(standing) => standing.squadId === squad.id
				);

				return {
					...squad,
					...standing,
					squadId: squad.id,
				};
			})
			.orderBy(["wins"], ["desc"])
			.value();
	};

	onSelectSquad = (squadId: number) => {
		if (!this._lpStore.hasLPPredictions) {
			createConnextraScript(ConnextraType.PICK_START, this._userStore.user?.id);
		}

		this._lpStore.setIsCardDisabled(true);
		this._lpStore.setSquadByStep(Number(squadId), this._step);
		this._lpStore.setIsPredictionsSaved(false);
		this._lpStore.setIsPredictionsChanged(true);

		const nextStep = this._lpStore.nextStep;

		if (nextStep) {
			this._lpStore.setCurrentStep(nextStep);
		}

		this.clearNextPrediction();

		setTimeout(() => {
			this._lpStore.setIsCardDisabled(false);
		}, 750);
	};

	private clearNextPrediction = () => {
		const predictions = this._lpStore.lightPrediction[this.conferenceType];
		const stepPosition = predictions.find(({type}) => type === this._step)?.position || 0;

		predictions.forEach((prediction) => {
			if (prediction.position > stepPosition) {
				prediction.squadId = 0;
			}

			return prediction;
		});

		this._lpStore.customSteps[this.conferenceType].wildCard = false;
		this._lpStore.customSteps[this.conferenceType].seeding = false;
	};
}
